import { useEffect, useRef } from 'react';
import type { FieldErrors } from 'react-hook-form';
import { toast } from 'sonner';
import { scrollToFirstFormError } from '@/utils/scrollToFirstFormError.ts';

/**
 * Walks a react-hook-form errors tree and collects every `message` string,
 * de-duplicated. Skips DOM `ref` nodes so we never recurse into (circular)
 * element references.
 */
const collectErrorMessages = (errors: FieldErrors): string[] => {
  const messages = new Set<string>();

  const visit = (node: unknown): void => {
    if (!node || typeof node !== 'object' || node instanceof Node) return;

    const message = (node as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) {
      messages.add(message);
      return;
    }

    Object.values(node).forEach(visit);
  };

  visit(errors);
  return [...messages];
};

/**
 * Feedback for a failed form submit: on every new invalid submit attempt it
 * shows a toast listing the validation messages and scrolls the first error
 * into view. Attach the returned ref to the `<form>` element.
 *
 * Only reacts to `submitCount` (a fresh submit) — never to live re-validation
 * on keystrokes — so the toast fires exactly once per save attempt.
 */
export const useInvalidSubmitFeedback = (
  formState: { submitCount: number; errors: FieldErrors },
  options?: { title?: string },
) => {
  const formRef = useRef<HTMLFormElement>(null);
  const title = options?.title ?? 'Nie można zapisać';
  const { submitCount, errors } = formState;

  useEffect(() => {
    if (submitCount === 0) return;

    const messages = collectErrorMessages(errors);
    if (messages.length === 0) return;

    toast.error(title, { description: messages.join(' • ') });

    scrollToFirstFormError(formRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitCount]);

  return formRef;
};

export default useInvalidSubmitFeedback;
